import { List } from 'immutable'
import * as React from 'react'
import {
  Action,
  any,
  async,
  AsyncState,
  fromJSX,
  loadingAsyncState,
  none,
  nothing,
  onlyIf,
  Option,
  some,
  Unit,
  unloadedAsyncState,
  wait,
  Widget
} from 'widgets-for-react'
import { setFlashMessage } from '../api/flash'

import {
  getPaymentStatus,
  IdealIssuerResponse,
  PaymentStatus,
  PaymentStatusResponse,
  setFlashAndFirstTerm,
  StartPaymentResponse
} from '../api/payments'
import {
  PaymentStatusProps,
  PaymentStatusState
} from '../components/containers/payments/payment_status_loader'
import { State } from '../components/containers/payments/show'
import { PageLoader } from '../components/shared/loaders/PageLoader'
import { PaymentMethod, TransactionStatus } from '../types/types'
import * as DL from '../utils/datalayer'
import SimpleFormat from '../utils/SimpleFormat'

type Props = {
  setState: (output: Action<State>) => void
  state: State
  paymentMethods: PaymentMethod[]
  onSubmit: () => void
}

const IsFormDisabled = (props: Props): boolean =>
  props.state.startPayment.kind === 'loading' ||
  props.state.startPayment.kind === 'loaded'

export const Show = (props: Props): JSX.Element => (
  <div className="page-section page-section--pt page-section--payment-methods">
    <div className="grid-container grid-container--medium">
      {props.state.startPayment.kind === 'failed' ? (
        <div className="box box--bordered box--warning">
          <div className="box__body">
            <SimpleFormat text={I18n.t('payments.start_payment_form.error')} />
          </div>
        </div>
      ) : (
        <></>
      )}

      <form
        onSubmit={x => {
          x.preventDefault()
          props.onSubmit()
        }}
        className="grid-100"
      >
        <fieldset className="input select optional box box--bordered">
          <legend className="page-section__head tac">
            <span className="page-section__title">
              {I18n.t('payments.start_payment_form.title')}
            </span>
          </legend>

          <div className="box box--brown">
            {props.paymentMethods.map(method => (
              <label className="box__body" key={method.id}>
                <div className="box__inner">
                  <div className="input boolean">
                    <input
                      type="radio"
                      value={method.name}
                      disabled={IsFormDisabled(props)}
                      checked={props.state.checkedPaymentMethod === method.name}
                      onChange={x =>
                        props.setState(s => ({
                          ...s,
                          checkedPaymentMethod: x.currentTarget.value
                        }))
                      }
                    />
                    <span>{I18n.t(`payments.methods.${method.name}`)}</span>
                  </div>
                  {method.name === 'ideal' &&
                  props.state.checkedPaymentMethod === method.name &&
                  props.state.idealIssuers.kind === 'loaded' ? (
                    <div>
                      <select
                        name="idealissuer"
                        disabled={IsFormDisabled(props)}
                        value={
                          !!props.state.checkedIdealIssuer
                            ? props.state.checkedIdealIssuer
                            : ''
                        }
                        onChange={e =>
                          props.setState(s => ({
                            ...s,
                            checkedIdealIssuer: e.currentTarget.value
                          }))
                        }
                        id="issuers-selector"
                      >
                        {props.state.idealIssuers.value.ideal_issuers.map(
                          issuer => (
                            <option key={issuer.bic} value={issuer.bic}>
                              {issuer.name}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>

                <img
                  className="box__image"
                  src={method.image}
                  alt={I18n.t(`payments.methods.${method.name}`)}
                  title={I18n.t(`payments.methods.${method.name}`)}
                />
              </label>
            ))}
          </div>
        </fieldset>
        <input
          type="submit"
          value={I18n.t('payments.start_payment_form.submit')}
          disabled={
            !props.state.checkedPaymentMethod ||
            (props.state.checkedPaymentMethod === 'ideal' &&
              !props.state.checkedIdealIssuer) ||
            IsFormDisabled(props)
          }
          className="btn btn--primary btn--block"
        />
      </form>
    </div>
  </div>
)

export const PaymentMethodsSkeletonScreen = () => {
  const item = (
    <label
      className="box__body"
      aria-label={I18n.t('search.infowindow.loading')}
    >
      <div className="box__inner">
        <div className="input boolean">
          <input type="radio" value="" disabled={true} checked={false} />
          <span className="skeleton">Loading method...</span>
        </div>
      </div>

      <div className="box__image skeleton" />
    </label>
  )
  return (
    <div className="page-section page-section--pt page-section--payment-methods page-section--skeleton">
      <div className="grid-container grid-container--medium">
        <div className="grid-100">
          <div className="input select optional box box--bordered">
            <p className="page-section__head tac">
              <span className="page-section__title skeleton">
                Loading the payment methods...
              </span>
            </p>

            <div className="box box--brown" aria-hidden="true">
              {item}
              {item}
              {item}
            </div>
          </div>
          <div className="btn btn--block skeleton" aria-hidden="true">
            Loading button...
          </div>
        </div>
      </div>
    </div>
  )
}

export const paymentMethodsLoader = (
  paymentMethods: AsyncState<PaymentMethod[]>
): Widget<Action<State>> =>
  async<PaymentMethod[]>(_ => nothing(), {
    loading: _ => nothing(),
    connectionError: fromJSX(_ => <div>Something went wrong</div>)
  })(paymentMethods).map(updateMethods => s => ({
    ...s,
    paymentMethods: updateMethods(s.paymentMethods)
  }))

export const idealIssuersLoader = (
  idealIssuers: AsyncState<IdealIssuerResponse>
): Widget<Action<State>> =>
  async<IdealIssuerResponse>(_ => nothing())(idealIssuers).map(
    updateIssuers => s => {
      const newIdealIssuers = updateIssuers(s.idealIssuers)
      return {
        ...s,
        idealIssuers: newIdealIssuers,
        checkedIdealIssuer:
          newIdealIssuers.kind === 'loaded' &&
          !!newIdealIssuers.value.ideal_issuers[0]
            ? newIdealIssuers.value.ideal_issuers[0].bic
            : null
      }
    }
  )

export const startPaymentLoader = (
  startPayment: AsyncState<StartPaymentResponse>
): Widget<Action<State>> =>
  async<StartPaymentResponse>()(startPayment).map(updateStart => s => {
    const newStartPayment = updateStart(s.startPayment)
    if (newStartPayment.kind === 'loaded' && newStartPayment.value.success) {
      DL.push({
        event: 'Betaalmodule',
        category: 'Betaalmodule',
        action: 'Betaalmethode',
        label: s.checkedPaymentMethod
      })
      window.location.href = newStartPayment.value.redirectUrl
    }
    return {
      ...s,
      startPayment: newStartPayment
    }
  })

const POLLING_LIMIT = 20

const getSanatizedStatus = (
  state: PaymentStatusState
): Option<TransactionStatus> =>
  state.status.kind !== 'loaded' || !state.status.value.status.status
    ? none()
    : some(
        state.status.value.status.is_paid_status &&
          !state.status.value.status.marked_as_paid
          ? 'pending'
          : state.status.value.status.status
      )

const shouldRedirect = (state: PaymentStatusState): boolean => {
  const status = getSanatizedStatus(state)
  return (
    state.pollCount > POLLING_LIMIT ||
    (status.v.kind === 'r' &&
      !List(['pending', 'created']).contains(status.v.v))
  )
}

export const waitingForStatusLoading = (
  props: PaymentStatusProps,
  state: PaymentStatusState
): Widget<Action<PaymentStatusState>> =>
  any<Action<PaymentStatusState>>()([
    async<Unit>()(state.setFlashMessage).map(u => pss => ({
      ...pss,
      setFlashMessage: u(pss.setFlashMessage)
    })),
    async<PaymentStatusResponse>(
      _ =>
        onlyIf(
          !shouldRedirect(state),
          wait<Action<AsyncState<PaymentStatusResponse>>>(
            state.pollCount === 0 ? 1000 : 3000
          )(() => _ =>
            loadingAsyncState(_ => getPaymentStatus(props.payment_id))
          )
        ),
      {
        loading: _ => nothing(),
        connectionError: nothing()
      }
    )(state.status).map(u => pss => ({
      ...pss,
      status: u(pss.status)
    }))
  ]).map(u => pss => {
    const newState = u(pss)
    newState.pollCount =
      pss.status.kind !== 'loaded' && newState.status.kind === 'loaded'
        ? newState.pollCount + 1
        : newState.pollCount

    const status = getSanatizedStatus(newState)

    if (newState.status.kind !== 'loading' && pss.status.kind === 'loading') {
      DL.push({
        event: 'Betaalmodule',
        category: 'Betaalmodule',
        action: 'Betalen',
        label:
          newState.status.kind === 'loaded' && status.v.kind === 'r'
            ? status.v.v
            : 'fout'
      })
    }
    if (
      pss.setFlashMessage.kind !== 'loaded' &&
      newState.setFlashMessage.kind === 'loaded' &&
      newState.status.kind === 'loaded'
    ) {
      window.location.href = newState.status.value.booking_url
    }

    return {
      ...newState,
      setFlashMessage:
        pss.status.kind !== 'loaded' &&
        newState.status.kind === 'loaded' &&
        shouldRedirect(newState)
          ? loadingAsyncState(_ =>
              setFlashAndFirstTerm(
                props.payment_id,
                'notice',
                `payments.flash.opp.${
                  status.v.kind === 'r' ? status.v.v : 'unknown'
                }`
              )
            )
          : newState.setFlashMessage
    }
  })
